/**
 * TrendCharts - Savings and Investments trend chart components
 * Last updated: March 20, 2026 - Fixed to always show at least current month
 */
import { useState } from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { BarChart3 } from "lucide-react";
import { getCurrentCurrency } from "../../utils/formatters";
import { getMonthKey } from "../../utils/dateHelpers";
import { THEME_COLORS } from "../../utils/constants";
import { getNetWorthFunds, getPendingForNetWorth } from "../../utils/netWorthMath";
import { usePurple } from "../../utils/purple";
import { usePlan } from "../../utils/plan"; // plan_09
import { investmentBalance, localDay, monthEndDay } from "../../utils/investMath"; // dark_13: one sum for investment balances
import { countsInProjection, signOf } from "../../utils/futureIncome"; // plan_25, plan_28

// Net Worth Chart from Monthly - generates data from monthly tracked items
// Includes toggle between actual and projected net worth
const ValueTooltip = ({ active, payload, fill }) => {
  if (!active || !payload || !payload.length) return null;
  const value = payload[0].value;
  if (value === null || value === undefined) return null;
  const symbol = getCurrentCurrency().symbol;
  const text = Math.abs(value) >= 1000
    ? `${symbol}${(value / 1000).toFixed(1)}k`
    : `${symbol}${Math.round(value)}`;
  return (
    <div
      style={{
        backgroundColor: fill,
        color: '#ffffff',
        borderRadius: '20px',
        padding: '3px 10px',
        fontSize: '12px',
        fontWeight: 500,
        fontFamily: "'DM Sans', sans-serif",
        whiteSpace: 'nowrap'
      }}
    >
      {text}
    </div>
  );
};

export const NetWorthChartFromMonthly = ({ sinkingFunds, investments, assets, debts, extraPayments, transactions = [], budgetSettings, theme }) => {
  const [showProjected, setShowProjected] = useState(false);
  const themeColors = THEME_COLORS[theme] || THEME_COLORS.pink;

  // Theme-aware colors for projected: each theme has a muted secondary paired with its primary
  const isWarm = theme === 'warm';
  const isPink = theme === 'pink';
  // Purple (admin-only): white chart card, purple line, lilac projected line.
  const purple = usePurple();
  const plan = usePlan(); // plan_09
  const projectedColor = plan ? '#E58AAE' : purple ? '#8F70E4' : isPink ? '#7A5FB0' : isWarm ? '#9B5A22' : '#6B4FB0';
  const projectedGridColor = plan ? '#F0EDF6' : purple ? '#F0E9F4' : isPink ? '#D6C9EC' : isWarm ? '#E3C7A8' : '#D3C6EE';
  const actualColor = plan ? '#E58AAE' : purple ? '#B49DE6' : isPink ? '#C23A72' : isWarm ? '#2D5A3D' : '#3A4DA8';

  // Get current month key for comparison
  const currentMonthKey = getMonthKey(new Date());
  const today = new Date();
  today.setHours(23, 59, 59, 999);

  // Get fund balance for a specific month based on transactions
  const getFundBalanceForMonth = (fundId, monthKey) => {
    const fund = (sinkingFunds || []).find(f => f.id === fundId);
    const startingBalance = fund?.starting_balance || 0;
    
    // For current month, use today's date; for past months, use end of month
    let cutoffDate;
    if (monthKey === currentMonthKey) {
      cutoffDate = today;
    } else {
      // Parse monthKey directly to avoid timezone issues with Date string parsing
      const [year, month] = monthKey.split('-').map(Number);
      cutoffDate = new Date(year, month, 0, 23, 59, 59);
    }
    
    const deposits = (transactions || [])
      .filter(t => {
        if (t.account_id !== fundId || t.account_type !== 'fund' || t.transaction_type !== 'deposit') return false;
        const tDate = new Date(t.date);
        return tDate <= cutoffDate;
      })
      .reduce((sum, t) => sum + t.amount, 0);
    
    const withdrawals = (transactions || [])
      .filter(t => {
        if (t.account_id !== fundId || t.account_type !== 'fund' || t.transaction_type !== 'withdrawal') return false;
        const tDate = new Date(t.date);
        return tDate <= cutoffDate;
      })
      .reduce((sum, t) => sum + t.amount, 0);
    
    const interest = (transactions || [])
      .filter(t => {
        if (t.interest_account_id !== fundId || t.interest_account_type !== 'fund' || !t.is_interest) return false;
        const tDate = new Date(t.date);
        return tDate <= cutoffDate;
      })
      .reduce((sum, t) => sum + t.amount, 0);
    
    return startingBalance + deposits + interest - withdrawals;
  };

  // Get investment balance for a specific month
  const getInvestmentBalanceForMonth = (invId, monthKey) => {
    const inv = (investments || []).find(i => i.id === invId);
    if (!inv) return 0;
    // dark_13: the latest manual balance carries over, plus what's added after it
    return investmentBalance(inv, transactions, monthKey, { cutoffDay: monthKey === currentMonthKey ? localDay(today) : monthEndDay(monthKey) });
  };

  // Get debt balance for a specific month
  const getDebtBalanceForMonth = (debt, monthKey) => {
    let startingBalance;
    if (debt.starting_balance !== undefined) {
      startingBalance = debt.starting_balance;
    } else {
      const monthKeys = Object.keys(debt.current_by_month || {}).sort();
      startingBalance = monthKeys.length > 0 
        ? debt.current_by_month[monthKeys[monthKeys.length - 1]] 
        : (debt.original_amount || 0);
    }
    
    // For current month, use today's date; for past months, use end of month
    let cutoffDate;
    if (monthKey === currentMonthKey) {
      cutoffDate = today;
    } else {
      // Parse monthKey directly to avoid timezone issues with Date string parsing
      const [year, month] = monthKey.split('-').map(Number);
      cutoffDate = new Date(year, month, 0, 23, 59, 59);
    }
    
    const totalPayments = (transactions || [])
      .filter(t => {
        if (!t.is_debt_payment || t.debt_id !== debt.id) return false;
        const tDate = new Date(t.date);
        return tDate <= cutoffDate;
      })
      .reduce((sum, t) => sum + t.amount, 0);
    
    return Math.max(0, startingBalance - totalPayments);
  };

  // Collect all unique months from all data sources
  const allMonths = new Set();

  // ALWAYS add current month first
  const currentMonth = getMonthKey(new Date());
  allMonths.add(currentMonth);

  // Add months from transactions (savings, investments, debt payments)
  (transactions || []).forEach(t => {
    if (!t.date) return;
    const month = t.date.slice(0, 7);
    
    // Include fund transactions
    if (t.account_type === 'fund') {
      allMonths.add(month);
    }
    // Include investment transactions
    if (t.account_type === 'investment') {
      allMonths.add(month);
    }
    // Include interest transactions
    if (t.interest_account_type === 'fund' || t.interest_account_type === 'investment') {
      allMonths.add(month);
    }
    // Include debt payments
    if (t.is_debt_payment) {
      allMonths.add(month);
    }
    // Include savings transactions that target accounts
    if (t.is_savings && t.account_id) {
      allMonths.add(month);
    }
  });

  // Legacy support: also check current_by_month on all item types
  const collectMonthsFromCurrentByMonth = (items) => {
    (items || []).forEach(item => {
      if (item.current_by_month) {
        Object.keys(item.current_by_month).forEach(month => allMonths.add(month));
      }
      if (item.override_by_month) {
        Object.keys(item.override_by_month).forEach(month => allMonths.add(month));
      }
    });
  };

  collectMonthsFromCurrentByMonth(sinkingFunds);
  collectMonthsFromCurrentByMonth(investments);
  collectMonthsFromCurrentByMonth(assets);
  collectMonthsFromCurrentByMonth(debts);

  if (allMonths.size < 1) {
    return (
      <div className="text-center py-8 text-gray-400">
        <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-30" />
        <p className="text-sm">Add data to see your trend chart</p>
      </div>
    );
  }

  // Sort months and generate chart data
  const sortedMonths = Array.from(allMonths).sort();

  // Get asset value for a month — falls back to most recent prior month if current month has no entry
  const getAssetValueForMonth = (item, month) => {
    if (item.current_by_month?.[month] !== undefined) return item.current_by_month[month];
    // Fall back to the most recent month that has a value
    const sortedKeys = Object.keys(item.current_by_month || {}).sort();
    const prior = sortedKeys.filter(k => k <= month);
    return prior.length > 0 ? item.current_by_month[prior[prior.length - 1]] : 0;
  };

  // Calculate cash available for a specific month (same formula as BudgetTab)
  const getCashAvailableForMonth = (monthKey) => {
    const monthTransactions = (transactions || []).filter(t => t.month_key === monthKey);
    const expenseTransactions = monthTransactions.filter(t => !t.is_income && !t.is_savings);
    const incomeTransactions = monthTransactions.filter(t => t.is_income && !t.is_from_savings && !t.is_interest);
    const savingsTransactions = monthTransactions.filter(t => t.is_savings);
    
    const totalSpent = expenseTransactions.reduce((sum, t) => sum + t.amount, 0);
    const totalIncome = incomeTransactions.reduce((sum, t) => sum + t.amount, 0);
    // Separate deposits from withdrawals in savings transactions
    const totalSavingsDeposits = savingsTransactions
      .filter(t => t.transaction_type !== 'withdrawal')
      .reduce((sum, t) => sum + t.amount, 0);
    const totalSavingsWithdrawals = savingsTransactions
      .filter(t => t.transaction_type === 'withdrawal')
      .reduce((sum, t) => sum + t.amount, 0);
    const leftoverFromLastMonth = budgetSettings?.leftover_by_month?.[monthKey] || 0;
    
    // Cash Available = Leftover + Income (excluding interest) + Withdrawals from Savings - Spent - Deposits to Savings
    return (leftoverFromLastMonth + totalIncome + totalSavingsWithdrawals) - totalSpent - totalSavingsDeposits;
  };

  // Funds switched off under "Count in net worth" are left out of every month,
  // past ones included, and their planned money is left out of Projected.
  const netWorthFunds = getNetWorthFunds(sinkingFunds);
  const totalPendingPayments = getPendingForNetWorth({ sinkingFunds, investmentAccounts: investments, extraPayments });

  const chartData = sortedMonths.map(month => {
    const savings = netWorthFunds.reduce((sum, fund) => sum + getFundBalanceForMonth(fund.id, month), 0);
    const investmentsTotal = (investments || []).reduce((sum, inv) => sum + getInvestmentBalanceForMonth(inv.id, month), 0);
    const assetsTotal = (assets || []).reduce((sum, asset) => sum + getAssetValueForMonth(asset, month), 0);
    const cashAvailable = getCashAvailableForMonth(month);
    const debtsTotal = (debts || []).reduce((sum, debt) => sum + getDebtBalanceForMonth(debt, month), 0);
    const totalAssets = savings + investmentsTotal + cashAvailable + assetsTotal;
    const netWorth = totalAssets - debtsTotal;
    
    // Projected = actual net worth + all pending extra payments
    const projectedNetWorth = netWorth + totalPendingPayments;
    
    return {
      month: month.slice(5) + '/' + month.slice(2, 4),
      netWorth,
      projectedNetWorth,
      totalAssets,
      debts: debtsTotal
    };
  });

  const dataKey = showProjected ? 'projectedNetWorth' : 'netWorth';
  const gradientId = showProjected ? 'projectedNetWorthGradient' : 'netWorthGradient2';
  const strokeColor = showProjected ? projectedColor : actualColor;
  const cardTint = plan ? '#FFFFFF' : purple ? '#FFFFFF' : showProjected
    ? (isPink ? '#E9E1F5' : isWarm ? '#F7E2CF' : '#E9E2F8')
    : (isPink ? '#FAE3EC' : isWarm ? '#E2EFE4' : '#E0E5FA');
  const gridColor = plan ? '#F0EDF6' : showProjected ? projectedGridColor : purple ? '#F0E9F4' : (isPink ? '#EFC5D7' : isWarm ? '#C2DCC9' : '#C4CDF0');

  return (
    <div className={plan ? "wy-plan-chart" : "rounded-2xl p-4 wy-tile"} style={plan ? {} : { backgroundColor: cardTint, ...(purple ? { boxShadow: '0 6px 22px rgba(120,60,150,0.09)', border: '1px solid #F5EFF8' } : {}) }}>
      {/* Toggle buttons */}
      <div className={plan ? "wy-plan-chartseg" : "flex gap-2 mb-3"}>
        <button
          onClick={() => setShowProjected(false)}
          className="px-3 py-1.5 text-xs font-medium rounded-full transition-all"
          style={{
            backgroundColor: !showProjected ? (actualColor) : ('#f3f4f6'),
            color: !showProjected ? 'white' : ('#4b5563')
          }}
        >
          Actual
        </button>
        <button
          onClick={() => setShowProjected(true)}
          className="px-3 py-1.5 text-xs font-medium rounded-full transition-all"
          style={{
            backgroundColor: showProjected ? (projectedColor) : ('#f3f4f6'),
            color: showProjected ? 'white' : ('#4b5563')
          }}
        >
          Projected
        </button>
      </div>

      <div className="h-44">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 18, right: 22, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="netWorthGradient2" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={actualColor} stopOpacity={plan ? 0.2 : 0.42}/>
                <stop offset="95%" stopColor={actualColor} stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="projectedNetWorthGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={projectedColor} stopOpacity={plan ? 0.2 : 0.42}/>
                <stop offset="95%" stopColor={projectedColor} stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} axisLine={false} interval={1} />
            <YAxis tick={{ fontSize: 12, fill: '#9ca3af' }} tickLine={false} axisLine={false} tickFormatter={(v) => `${getCurrentCurrency().symbol}${(v/1000).toFixed(0)}k`} />
            <Tooltip
              cursor={{ stroke: strokeColor, strokeWidth: 1, strokeDasharray: '3 3' }}
              content={<ValueTooltip fill={plan ? '#26252E' : strokeColor} />}
              offset={14}
              allowEscapeViewBox={{ x: false, y: true }}
            />
            <Area 
              type="monotone" 
              dataKey={dataKey} 
              stroke={strokeColor} 
              strokeWidth={plan ? 2.2 : 3} 
              fill={`url(#${gradientId})`}
              activeDot={{ r: 4, strokeWidth: 2, stroke: cardTint, fill: strokeColor }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      {plan && (() => {
        // plan_09: one calm line under the chart
        const vals = chartData.map((d) => d[dataKey]).filter((v) => typeof v === 'number');
        if (vals.length < 1) return null;
        const first = vals[0];
        const last = vals[vals.length - 1];
        const diff = last - first;
        const sym = getCurrentCurrency().symbol;
        const money = (n) => `${sym}${Math.round(Math.abs(n)).toLocaleString('en-US')}`;
        const since = new Date(`${sortedMonths[0]}-01T12:00:00`).toLocaleDateString('en-US', { month: 'long' });
        const text = showProjected
          ? 'Includes money you\u2019ve planned to move in'
          : vals.length < 2 || Math.round(diff) === 0
            ? `No change since ${since}`
            : `${diff > 0 ? 'Up' : 'Down'} ${money(diff)} since ${since}`;
        return <p style={{ margin: '6px 0 0', fontSize: 12.5, color: '#8B879A' }} data-testid="plan-chart-caption">{text}</p>;
      })()}
    </div>
  );
};


// Savings Trend Chart - shows total savings over time
// Includes toggle between actual and projected savings
export const SavingsTrendChart = ({ sinkingFunds, extraPayments, transactions = [], theme = 'pink' }) => {
  const [showProjected, setShowProjected] = useState(false);

  // Theme-aware colors: primary from THEME_COLORS matches the rest of the app; projected is a muted companion
  const themeColors = THEME_COLORS[theme] || THEME_COLORS.pink;
  const isWarm = theme === 'warm';
  const isPink = theme === 'pink';
  // Purple (admin-only): white chart card, purple line, lilac projected line.
  const purple = usePurple();
  const plan = usePlan(); // plan_09
  const actualColor = plan ? '#7FA6DE' : purple ? '#B49DE6' : isPink ? '#C23A72' : isWarm ? '#2D5A3D' : '#3A4DA8';
  const projectedColor = plan ? '#7FA6DE' : purple ? '#8F70E4' : isPink ? '#7A5FB0' : isWarm ? '#9B5A22' : '#6B4FB0';
  const actualGridColor = purple ? '#F0E9F4' : isPink ? '#EFC5D7' : isWarm ? '#C2DCC9' : '#C4CDF0';
  const projectedGridColor = plan ? '#F0EDF6' : purple ? '#F0E9F4' : isPink ? '#D6C9EC' : isWarm ? '#E3C7A8' : '#D3C6EE';

  // Get current month key for comparison
  const currentMonthKey = getMonthKey(new Date());
  const today = new Date();
  today.setHours(23, 59, 59, 999);

  // Get fund balance for a specific month based on transactions
  const getFundBalanceForMonth = (fundId, monthKey) => {
    const fund = (sinkingFunds || []).find(f => f.id === fundId);
    const startingBalance = fund?.starting_balance || 0;
    
    // For current month, use today's date; for past months, use end of month
    let cutoffDate;
    if (monthKey === currentMonthKey) {
      cutoffDate = today;
    } else {
      // Parse monthKey directly to avoid timezone issues with Date string parsing
      const [year, month] = monthKey.split('-').map(Number);
      // month is 1-indexed from the string (01-12), but Date uses 0-indexed
      // new Date(year, month, 0) gives last day of previous month, so month (1-indexed) gives last day of that month
      cutoffDate = new Date(year, month, 0, 23, 59, 59);
    }
    
    const deposits = (transactions || [])
      .filter(t => {
        if (t.account_id !== fundId || t.account_type !== 'fund' || t.transaction_type !== 'deposit') return false;
        const tDate = new Date(t.date);
        return tDate <= cutoffDate;
      })
      .reduce((sum, t) => sum + t.amount, 0);
    
    const withdrawals = (transactions || [])
      .filter(t => {
        if (t.account_id !== fundId || t.account_type !== 'fund' || t.transaction_type !== 'withdrawal') return false;
        const tDate = new Date(t.date);
        return tDate <= cutoffDate;
      })
      .reduce((sum, t) => sum + t.amount, 0);
    
    const interest = (transactions || [])
      .filter(t => {
        if (t.interest_account_id !== fundId || t.interest_account_type !== 'fund' || !t.is_interest) return false;
        const tDate = new Date(t.date);
        return tDate <= cutoffDate;
      })
      .reduce((sum, t) => sum + t.amount, 0);
    
    return startingBalance + deposits + interest - withdrawals;
  };

  // Collect all unique months from sinking fund transactions
  const allMonths = new Set();

  // ALWAYS add current month first
  const currentMonth = getMonthKey(new Date());
  allMonths.add(currentMonth);

  // Add months from savings transactions (deposits, withdrawals, interest)
  (transactions || []).forEach(t => {
    if (!t.date) return;
    const month = t.date.slice(0, 7);
    
    // Include if it's a fund transaction (deposit/withdrawal)
    if (t.account_type === 'fund') {
      allMonths.add(month);
    }
    // Include if it's interest applied to a fund
    if (t.interest_account_type === 'fund') {
      allMonths.add(month);
    }
    // Include if it's marked as a savings transaction targeting a fund
    if (t.is_savings && t.account_id) {
      allMonths.add(month);
    }
  });

  // Legacy support: also check current_by_month
  (sinkingFunds || []).forEach(fund => {
    if (fund.current_by_month) {
      Object.keys(fund.current_by_month).forEach(month => allMonths.add(month));
    }
  });

  if (allMonths.size < 1) {
    return (
      <div className="text-center py-8 text-gray-400">
        <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-30" />
        <p className="text-sm">Add data to see your savings trend</p>
      </div>
    );
  }

  // Sort months and generate chart data
  const sortedMonths = Array.from(allMonths).sort();

  // Calculate total of all pending extra payments for the year (only fund allocations)
  const totalPendingPayments = (extraPayments || [])
    .filter(p => !p.moved_to_account && countsInProjection(p))
    .reduce((sum, p) => {
      if (p.fund_allocations) {
        return sum + signOf(p) * Object.values(p.fund_allocations).reduce((s, amt) => s + amt, 0);
      }
      return sum + signOf(p) * (p.amount || 0);
    }, 0);

  const chartData = sortedMonths.map((month) => {
    const totalSavings = (sinkingFunds || []).reduce((sum, fund) => {
      return sum + getFundBalanceForMonth(fund.id, month);
    }, 0);
    
    // Projected = actual savings for that month + all pending payments for the year
    const projectedSavings = totalSavings + totalPendingPayments;
    
    return {
      month: month.slice(5) + '/' + month.slice(2, 4),
      savings: totalSavings,
      projected: projectedSavings
    };
  });

  const dataKey = showProjected ? 'projected' : 'savings';
  const gradientId = showProjected ? 'projectedGradient' : 'savingsGradient';
  const strokeColor = showProjected ? projectedColor : actualColor;
  const cardTint = plan ? '#FFFFFF' : purple ? '#FFFFFF' : showProjected
    ? (isPink ? '#E9E1F5' : isWarm ? '#F7E2CF' : '#E9E2F8')
    : (isPink ? '#FAE3EC' : isWarm ? '#E2EFE4' : '#E0E5FA');
  const gridColor = plan ? '#F0EDF6' : showProjected ? projectedGridColor : actualGridColor;

  return (
    <div className={plan ? "wy-plan-chart" : "rounded-2xl p-4 wy-tile"} style={plan ? {} : { backgroundColor: cardTint, ...(purple ? { boxShadow: '0 6px 22px rgba(120,60,150,0.09)', border: '1px solid #F5EFF8' } : {}) }}>
      {/* Toggle buttons */}
      <div className={plan ? "wy-plan-chartseg" : "flex gap-2 mb-3"}>
        <button
          onClick={() => setShowProjected(false)}
          className="px-3 py-1.5 text-xs font-medium rounded-full transition-all"
          style={{ 
            backgroundColor: !showProjected ? (actualColor) : ('#f3f4f6'),
            color: !showProjected ? 'white' : ('#4b5563')
          }}
        >
          Actual
        </button>
        <button
          onClick={() => setShowProjected(true)}
          className="px-3 py-1.5 text-xs font-medium rounded-full transition-all"
          style={{ 
            backgroundColor: showProjected ? (projectedColor) : ('#f3f4f6'),
            color: showProjected ? 'white' : ('#4b5563')
          }}
        >
          Projected
        </button>
      </div>

      <div className="h-40">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 18, right: 22, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="savingsGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={actualColor} stopOpacity={plan ? 0.2 : 0.42}/>
                <stop offset="95%" stopColor={actualColor} stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="projectedGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={projectedColor} stopOpacity={plan ? 0.2 : 0.42}/>
                <stop offset="95%" stopColor={projectedColor} stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} axisLine={false} interval={1} />
            <YAxis tick={{ fontSize: 12, fill: '#9ca3af' }} tickLine={false} axisLine={false} tickFormatter={(v) => `${getCurrentCurrency().symbol}${(v/1000).toFixed(0)}k`} />
            <Tooltip
              cursor={{ stroke: strokeColor, strokeWidth: 1, strokeDasharray: '3 3' }}
              content={<ValueTooltip fill={plan ? '#26252E' : strokeColor} />}
              offset={14}
              allowEscapeViewBox={{ x: false, y: true }}
            />
            <Area 
              type="monotone" 
              dataKey={dataKey} 
              stroke={strokeColor} 
              strokeWidth={plan ? 2.2 : 3} 
              fill={`url(#${gradientId})`}
              activeDot={{ r: 4, strokeWidth: 2, stroke: cardTint, fill: strokeColor }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      {plan && (() => {
        // plan_09: one calm line under the chart
        const vals = chartData.map((d) => d[dataKey]).filter((v) => typeof v === 'number');
        if (vals.length < 1) return null;
        const first = vals[0];
        const last = vals[vals.length - 1];
        const diff = last - first;
        const sym = getCurrentCurrency().symbol;
        const money = (n) => `${sym}${Math.round(Math.abs(n)).toLocaleString('en-US')}`;
        const since = new Date(`${sortedMonths[0]}-01T12:00:00`).toLocaleDateString('en-US', { month: 'long' });
        const text = showProjected
          ? 'Includes money you\u2019ve planned to move in'
          : vals.length < 2 || Math.round(diff) === 0
            ? `No change since ${since}`
            : `${diff > 0 ? 'Up' : 'Down'} ${money(diff)} since ${since}`;
        return <p style={{ margin: '6px 0 0', fontSize: 12.5, color: '#8B879A' }} data-testid="plan-chart-caption">{text}</p>;
      })()}
    </div>
  );
};

// Investments Trend Chart - shows total investments over time
// Includes toggle between actual and projected investments
export const InvestmentsTrendChart = ({ investmentAccounts, extraPayments, transactions = [], theme = 'pink' }) => {
  const [showProjected, setShowProjected] = useState(false);

  // Theme-aware colors: primary from THEME_COLORS matches the rest of the app; projected is a muted companion
  const themeColors = THEME_COLORS[theme] || THEME_COLORS.pink;
  const isWarm = theme === 'warm';
  const isPink = theme === 'pink';
  // Purple (admin-only): white chart card, purple line, lilac projected line.
  const purple = usePurple();
  const plan = usePlan(); // plan_09
  const actualColor = plan ? '#A68EEA' : purple ? '#B49DE6' : isPink ? '#C23A72' : isWarm ? '#2D5A3D' : '#3A4DA8';
  const projectedColor = plan ? '#A68EEA' : purple ? '#8F70E4' : isPink ? '#7A5FB0' : isWarm ? '#9B5A22' : '#6B4FB0';
  const actualGridColor = purple ? '#F0E9F4' : isPink ? '#EFC5D7' : isWarm ? '#C2DCC9' : '#C4CDF0';
  const projectedGridColor = plan ? '#F0EDF6' : purple ? '#F0E9F4' : isPink ? '#D6C9EC' : isWarm ? '#E3C7A8' : '#D3C6EE';

  // Get current month key for comparison
  const currentMonthKey = getMonthKey(new Date());
  const today = new Date();
  today.setHours(23, 59, 59, 999);

  // Get investment balance for a specific month
  const getInvestmentBalanceForMonth = (invId, monthKey) => {
    const inv = (investmentAccounts || []).find(i => i.id === invId);
    if (!inv) return 0;
    // dark_13: the latest manual balance carries over, plus what's added after it
    return investmentBalance(inv, transactions, monthKey, { cutoffDay: monthKey === currentMonthKey ? localDay(today) : monthEndDay(monthKey) });
  };

  // Collect all unique months from investment overrides and transactions
  const allMonths = new Set();

  (investmentAccounts || []).forEach(inv => {
    if (inv.override_by_month) {
      Object.keys(inv.override_by_month).forEach(month => allMonths.add(month));
    }
  });

  // Also add months from transactions
  (transactions || []).forEach(t => {
    if ((t.account_type === 'investment' || t.interest_account_type === 'investment') && t.date) {
      const month = t.date.slice(0, 7);
      allMonths.add(month);
    }
  });

  // ALWAYS add current month
  const currentMonth = getMonthKey(new Date());
  allMonths.add(currentMonth);

  if (allMonths.size < 1) {
    return (
      <div className="text-center py-8 text-gray-400">
        <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-30" />
        <p className="text-sm">Add data to see your investments trend</p>
      </div>
    );
  }

  // Sort months and generate chart data
  const sortedMonths = Array.from(allMonths).sort();

  // Calculate total of all pending investment allocations
  const totalPendingInvestments = (extraPayments || [])
    .filter(p => !p.moved_to_account && p.investment_allocations && countsInProjection(p))
    .reduce((sum, p) => {
      const invTotal = Object.values(p.investment_allocations || {}).reduce((s, amt) => s + amt, 0);
      return sum + invTotal;
    }, 0);

  const chartData = sortedMonths.map((month) => {
    const totalInvestments = (investmentAccounts || []).reduce((sum, inv) => {
      return sum + getInvestmentBalanceForMonth(inv.id, month);
    }, 0);
    
    // Projected = actual investments for that month + all pending investment allocations
    const projectedInvestments = totalInvestments + totalPendingInvestments;
    
    return {
      month: month.slice(5) + '/' + month.slice(2, 4),
      investments: totalInvestments,
      projected: projectedInvestments
    };
  });

  const dataKey = showProjected ? 'projected' : 'investments';
  const gradientId = showProjected ? 'investmentProjectedGradient' : 'investmentActualGradient';
  const strokeColor = showProjected ? projectedColor : actualColor;
  const cardTint = plan ? '#FFFFFF' : purple ? '#FFFFFF' : showProjected
    ? (isPink ? '#E9E1F5' : isWarm ? '#F7E2CF' : '#E9E2F8')
    : (isPink ? '#FAE3EC' : isWarm ? '#E2EFE4' : '#E0E5FA');
  const gridColor = plan ? '#F0EDF6' : showProjected ? projectedGridColor : actualGridColor;

  return (
    <div className={plan ? "wy-plan-chart" : "rounded-2xl p-4 wy-tile"} style={plan ? {} : { backgroundColor: cardTint, ...(purple ? { boxShadow: '0 6px 22px rgba(120,60,150,0.09)', border: '1px solid #F5EFF8' } : {}) }}>
      {/* Toggle buttons */}
      <div className={plan ? "wy-plan-chartseg" : "flex gap-2 mb-3"}>
        <button
          onClick={() => setShowProjected(false)}
          className="px-3 py-1.5 text-xs font-medium rounded-full transition-all"
          style={{ 
            backgroundColor: !showProjected ? (actualColor) : ('#f3f4f6'),
            color: !showProjected ? 'white' : ('#4b5563')
          }}
        >
          Actual
        </button>
        <button
          onClick={() => setShowProjected(true)}
          className="px-3 py-1.5 text-xs font-medium rounded-full transition-all"
          style={{ 
            backgroundColor: showProjected ? (projectedColor) : ('#f3f4f6'),
            color: showProjected ? 'white' : ('#4b5563')
          }}
        >
          Projected
        </button>
      </div>

      <div className="h-40">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 18, right: 22, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="investmentActualGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={actualColor} stopOpacity={plan ? 0.2 : 0.42}/>
                <stop offset="95%" stopColor={actualColor} stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="investmentProjectedGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={projectedColor} stopOpacity={plan ? 0.2 : 0.42}/>
                <stop offset="95%" stopColor={projectedColor} stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} axisLine={false} interval={1} />
            <YAxis tick={{ fontSize: 12, fill: '#9ca3af' }} tickLine={false} axisLine={false} tickFormatter={(v) => `${getCurrentCurrency().symbol}${(v/1000).toFixed(0)}k`} />
            <Tooltip
              cursor={{ stroke: strokeColor, strokeWidth: 1, strokeDasharray: '3 3' }}
              content={<ValueTooltip fill={plan ? '#26252E' : strokeColor} />}
              offset={14}
              allowEscapeViewBox={{ x: false, y: true }}
            />
            <Area 
              type="monotone" 
              dataKey={dataKey} 
              stroke={strokeColor} 
              strokeWidth={plan ? 2.2 : 3} 
              fill={`url(#${gradientId})`}
              activeDot={{ r: 4, strokeWidth: 2, stroke: cardTint, fill: strokeColor }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      {plan && (() => {
        // plan_09: one calm line under the chart
        const vals = chartData.map((d) => d[dataKey]).filter((v) => typeof v === 'number');
        if (vals.length < 1) return null;
        const first = vals[0];
        const last = vals[vals.length - 1];
        const diff = last - first;
        const sym = getCurrentCurrency().symbol;
        const money = (n) => `${sym}${Math.round(Math.abs(n)).toLocaleString('en-US')}`;
        const since = new Date(`${sortedMonths[0]}-01T12:00:00`).toLocaleDateString('en-US', { month: 'long' });
        const text = showProjected
          ? 'Includes money you\u2019ve planned to move in'
          : vals.length < 2 || Math.round(diff) === 0
            ? `No change since ${since}`
            : `${diff > 0 ? 'Up' : 'Down'} ${money(diff)} since ${since}`;
        return <p style={{ margin: '6px 0 0', fontSize: 12.5, color: '#8B879A' }} data-testid="plan-chart-caption">{text}</p>;
      })()}
    </div>
  );
};
